import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Container,
  Alert,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Tooltip,
  TablePagination
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon, Search as SearchIcon, Publish as PublishIcon, Videocam as VideocamIcon, PictureAsPdf as PictureAsPdfIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { fetchSchemesPage, fetchSchemesByTitle, fetchSchemeBySerialNo, deleteScheme, deleteYojanaApproval, publishSchemes, fetchYojanaReport, normalizeYojanaListResponse } from '../../utils/api';
import SchemeForm from './SchemeForm';
import { usePermissions } from '../../hooks/usePermissions';

const SchemesList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkPermission, handlePermissionAction, isYojanaAdmin } = usePermissions();
  const hasHandledEditState = useRef(false);

  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedSchemes, setSearchedSchemes] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState({ open: false, text: '', severity: 'success' });
  const [yojanaReport, setYojanaReport] = useState({ totalYojana: 0, central: 0, state: 0, local: 0 });
  const [activeGovernmentFilter, setActiveGovernmentFilter] = useState('all');
  const [createPrefillDraft, setCreatePrefillDraft] = useState(null);
  const [schemeFormInstanceId, setSchemeFormInstanceId] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const parseYojanaReport = (data) => {
    const arr = Array.isArray(data) ? data : [];
    const byGovt = {};
    arr.forEach(({ yojanaGovernment, totalCount }) => {
      byGovt[yojanaGovernment] = totalCount;
    });
    return {
      totalYojana: byGovt.totalYojana ?? 0,
      central: byGovt.Central ?? 0,
      state: byGovt.State ?? 0,
      local: byGovt.Local ?? 0
    };
  };

  const normalizeGovernment = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw === 'central') return 'central';
    if (raw === 'state') return 'state';
    if (raw === 'local') return 'local';
    return raw;
  };

  const getSchemeGovernment = (scheme) =>
    normalizeGovernment(scheme?.government ?? scheme?.yojanaGovernment ?? scheme?.filters?.government);

  useEffect(() => {
    let cancelled = false;
    const loadYojanaSummary = async () => {
      try {
        const yojanaResponse = await fetchYojanaReport();
        if (cancelled) return;
        setYojanaReport(parseYojanaReport(yojanaResponse.data));
      } catch (err) {
        console.error('Error fetching yojana summary:', err);
      }
    };
    loadYojanaSummary();
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger]);

  useEffect(() => {
    let cancelled = false;
    const loadSchemesPage = async () => {
      setLoading(true);
      hasHandledEditState.current = false;
      try {
        const schemesResponse = await fetchSchemesPage({ pageNo: page, pageSize: rowsPerPage });
        if (cancelled) return;
        const payload = schemesResponse?.data;
        const content = Array.isArray(payload?.content) ? payload.content : [];
        setSchemes(content);
        const totalElements =
          payload?.page?.totalElements ??
          payload?.totalElements ??
          content.length;
        setTotalCount(totalElements);
        setError(null);
      } catch (err) {
        console.error('Error fetching schemes:', err);
        if (!cancelled) {
          setError('Failed to load schemes. Please try again later.');
          setSchemes([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSchemesPage();
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [activeGovernmentFilter]);

  // From My Yojana Requests: edit by yojanaId, or resubmit rejected create using updatedDataJson
  useEffect(() => {
    if (loading || hasHandledEditState.current || !checkPermission('YOJNA', 'write')) return;

    const st = location.state;
    const hasResubmitDraft =
      st && Object.prototype.hasOwnProperty.call(st, 'resubmitCreateDraft');
    if (hasResubmitDraft) {
      hasHandledEditState.current = true;
      setSelectedScheme(null);
      setCreatePrefillDraft(st.resubmitCreateDraft ?? null);
      setSchemeFormInstanceId((n) => n + 1);
      setOpenDialog(true);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const editYojanaId = st?.editYojanaId;
    if (!editYojanaId) return;

    const scheme = schemes.find((s) => String(s.yojnaId) === String(editYojanaId));
    if (scheme) {
      hasHandledEditState.current = true;
      setCreatePrefillDraft(null);
      setSelectedScheme(scheme);
      setSchemeFormInstanceId((n) => n + 1);
      setOpenDialog(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [loading, schemes, location.state, location.pathname, navigate, checkPermission]);

  // Handle search button click - use API endpoint
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchedSchemes([]);
      setHasSearched(false);
      return;
    }

    setHasSearched(true);
    setSearchLoading(true);
    setError(null);

    const query = searchQuery.trim();
    const isNumericOnly = /^\d+$/.test(query);

    try {
      const response = isNumericOnly
        ? await fetchSchemeBySerialNo(query)
        : await fetchSchemesByTitle(query);
      // The API might return an array or a single object
      const result = response.data;
      setSearchedSchemes(normalizeYojanaListResponse(result));
    } catch (err) {
      console.error('Error searching schemes:', err);
      setSearchedSchemes([]);
      // Don't show error if it's just "not found" - that's expected
      if (err.response?.status !== 404) {
        setError('Failed to search schemes. Please try again later.');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  if (!checkPermission('YOJNA', 'read')) {
    return (
      <Container>
        <Alert severity="warning">
          You do not have permission to view schemes.
        </Alert>
      </Container>
    );
  }

  const handleOpenDialog = (scheme = null) => {
    if (scheme) {
      handlePermissionAction('YOJNA', 'write', () => {
        setCreatePrefillDraft(null);
        setSelectedScheme(scheme);
        setSchemeFormInstanceId((n) => n + 1);
        setOpenDialog(true);
      });
    } else {
      handlePermissionAction('YOJNA', 'write', () => {
        setCreatePrefillDraft(null);
        setSelectedScheme(null);
        setSchemeFormInstanceId((n) => n + 1);
        setOpenDialog(true);
      });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedScheme(null);
    setCreatePrefillDraft(null);
  };

  const handleSchemeAdded = () => {
    // Refresh base schemes list and reset any active search view
    setRefreshTrigger(prev => prev + 1);
    setHasSearched(false);
    setSearchedSchemes([]);
    handleCloseDialog();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGovernmentFilterClick = (filter) => {
    setActiveGovernmentFilter(filter);
    setPage(0);
  };

  const handleDeleteScheme = async (scheme) => {
    if (window.confirm('Are you sure you want to delete this scheme?')) {
      try {
        if (isYojanaAdmin()) {
          await deleteScheme(scheme.yojnaId, {
            previousData: scheme,
            identifier: scheme.title
          });
        } else {
          await deleteYojanaApproval(scheme.yojnaId, scheme, {
            identifier: scheme.title || scheme.yojnaId
          });
        }
        setRefreshTrigger(prev => prev + 1);
      } catch (err) {
        console.error('Error deleting scheme:', err);
        alert('Failed to delete scheme. Please try again.');
      }
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Are you sure you want to publish schemes? This will clear the cache.')) {
      return;
    }

    setPublishing(true);
    setPublishMessage({ open: false, text: '', severity: 'success' });

    try {
      const response = await publishSchemes();
      setPublishMessage({
        open: true,
        text: 'Schemes published successfully!',
        severity: 'success'
      });
      // Refresh schemes list after publishing
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error publishing schemes:', err);
      setPublishMessage({
        open: true,
        text: err.response?.data?.message || 'Failed to publish schemes. Please try again.',
        severity: 'error'
      });
    } finally {
      setPublishing(false);
    }
  };

  const handleClosePublishMessage = () => {
    setPublishMessage({ ...publishMessage, open: false });
  };

  const schemeCounts = {
    total: yojanaReport.totalYojana,
    central: yojanaReport.central,
    state: yojanaReport.state,
    local: yojanaReport.local
  };

  const applyGovernmentFilter = (items) => {
    if (activeGovernmentFilter === 'all') return items;
    return items.filter((scheme) => getSchemeGovernment(scheme) === activeGovernmentFilter);
  };

  const filteredSchemes = applyGovernmentFilter(schemes);
  const filteredSearchedSchemes = applyGovernmentFilter(searchedSchemes);

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Schemes
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {checkPermission('YOJNA', 'write') && (
            <>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={publishing ? <CircularProgress size={20} /> : <PublishIcon />}
                onClick={handlePublish}
                disabled={publishing}
              >
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add New Scheme
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Schemes Summary */}
      {!loading && !error && (
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" component="div" gutterBottom>
              Schemes Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper
                  onClick={() => handleGovernmentFilterClick('all')}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: activeGovernmentFilter === 'all' ? '#e8eaf6' : '#f5f5f5',
                    cursor: 'pointer',
                    border: activeGovernmentFilter === 'all' ? '2px solid #3f51b5' : '2px solid transparent'
                  }}
                >
                  <Typography variant="h4" color="primary">
                    {schemeCounts.total}
                  </Typography>
                  <Typography variant="body2">Total Yojana</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  onClick={() => handleGovernmentFilterClick('central')}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: activeGovernmentFilter === 'central' ? '#bbdefb' : '#e3f2fd',
                    cursor: 'pointer',
                    border: activeGovernmentFilter === 'central' ? '2px solid #1976d2' : '2px solid transparent'
                  }}
                >
                  <Typography variant="h4" color="primary">
                    {schemeCounts.central}
                  </Typography>
                  <Typography variant="body2">Central Govt</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  onClick={() => handleGovernmentFilterClick('state')}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: activeGovernmentFilter === 'state' ? '#b2ebf2' : '#e0f7fa',
                    cursor: 'pointer',
                    border: activeGovernmentFilter === 'state' ? '2px solid #00838f' : '2px solid transparent'
                  }}
                >
                  <Typography variant="h4" color="secondary">
                    {schemeCounts.state}
                  </Typography>
                  <Typography variant="body2">State Govt</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  onClick={() => handleGovernmentFilterClick('local')}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: activeGovernmentFilter === 'local' ? '#dcedc8' : '#f1f8e9',
                    cursor: 'pointer',
                    border: activeGovernmentFilter === 'local' ? '2px solid #689f38' : '2px solid transparent'
                  }}
                >
                  <Typography variant="h4" color="success.main">
                    {schemeCounts.local}
                  </Typography>
                  <Typography variant="body2">Local Govt</Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Search Box */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search schemes by title or description"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setHasSearched(false);
            setSearchedSchemes([]);
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, bgcolor: '#fff9f9' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : hasSearched ? (
        // Show search results
        <Box sx={{ mt: 3 }}>
          {searchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredSearchedSchemes.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>S.No.</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Media</TableCell>
                    <TableCell align="center">Status</TableCell>
                    {checkPermission('YOJNA', 'write') && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSearchedSchemes.map((scheme) => (
                    <TableRow key={scheme.serialNo ?? scheme.yojnaId}>
                      <TableCell>{scheme.serialNo}</TableCell>
                      <TableCell>{scheme.title}</TableCell>
                      <TableCell>{scheme.description}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {scheme.yojanaVideoUrl && (
                            <Tooltip title="Video uploaded">
                              <VideocamIcon color="action" fontSize="small" />
                            </Tooltip>
                          )}
                          {scheme.grLink && (
                            <Tooltip title="GR / PDF uploaded">
                              <PictureAsPdfIcon color="action" fontSize="small" />
                            </Tooltip>
                          )}
                          {!scheme.yojanaVideoUrl && !scheme.grLink && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {scheme.status === 'Completed' ? (
                          <Tooltip title="Yojana information completed">
                            <CheckCircleIcon color="success" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      {checkPermission('YOJNA', 'write') && (
                        <TableCell>
                          <IconButton onClick={() => handleOpenDialog(scheme)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteScheme(scheme)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
              <Typography variant="body1" color="textSecondary">
                {activeGovernmentFilter === 'all'
                  ? 'No Yojana Found'
                  : `No ${activeGovernmentFilter} government yojana found`}
              </Typography>
            </Paper>
          )}
        </Box>
      ) : filteredSchemes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>
            {activeGovernmentFilter === 'all'
              ? 'No schemes found. Add your first scheme!'
              : `No ${activeGovernmentFilter} government schemes found.`}
          </Typography>
        </Paper>
      ) : (
        <>
          {activeGovernmentFilter !== 'all' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Government filter applies to schemes on this page. Use search for a specific yojana.
            </Typography>
          )}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Media</TableCell>
                  <TableCell align="center">Status</TableCell>
                  {checkPermission('YOJNA', 'write') && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSchemes.map((scheme) => (
                  <TableRow key={scheme.serialNo ?? scheme.yojnaId}>
                    <TableCell>{scheme.serialNo}</TableCell>
                    <TableCell>{scheme.title}</TableCell>
                    <TableCell>{scheme.description}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {scheme.yojanaVideoUrl && (
                          <Tooltip title="Video uploaded">
                            <VideocamIcon color="action" fontSize="small" />
                          </Tooltip>
                        )}
                        {scheme.grLink && (
                          <Tooltip title="GR / PDF uploaded">
                            <PictureAsPdfIcon color="action" fontSize="small" />
                          </Tooltip>
                        )}
                        {!scheme.yojanaVideoUrl && !scheme.grLink && (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {scheme.status === 'Completed' ? (
                        <Tooltip title="Yojana information completed">
                          <CheckCircleIcon color="success" fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    {checkPermission('YOJNA', 'write') && (
                      <TableCell>
                        <IconButton onClick={() => handleOpenDialog(scheme)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteScheme(scheme)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
        </>
      )}

      {/* Add/Edit Scheme Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedScheme ? 'Edit Scheme' : createPrefillDraft ? 'Resubmit scheme' : 'Add New Scheme'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <SchemeForm
            key={schemeFormInstanceId}
            scheme={selectedScheme}
            prefillDraft={createPrefillDraft}
            onSuccess={handleSchemeAdded}
          />
        </DialogContent>
      </Dialog>

      {/* Publish Snackbar */}
      <Snackbar
        open={publishMessage.open}
        autoHideDuration={6000}
        onClose={handleClosePublishMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleClosePublishMessage} 
          severity={publishMessage.severity}
          sx={{ width: '100%' }}
        >
          {publishMessage.text}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SchemesList; 